import { useEffect, useState, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";

import {
  fileURL,
  getGalleryPage,
  getSettings,
  sendMessage,
} from "../services/api";
import BackgroundView from "../components/BackgroundView";
import { BASE_URL } from "../constants/config";
import MediaCard from "../components/MediaCard";
import Swal from "sweetalert2";
import { getEcho } from "../pusher/echo";

function GalleryPage() {
  const navigate = useNavigate();

  const params = new URLSearchParams(window.location.search);
  const folder = params.get("folder");

  const [settingsLoading, setSettingsLoading] = useState(true);
  const [pageLoading, setPageLoading] = useState(false);

  const [settings, setSettings] = useState(null);
  const [gallery, setGallery] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);

  // prevents double requests
  const loadingRef = useRef(false);

  // ignore old async responses when folder changes
  const requestIdRef = useRef(0);

  // keeps latest hasMore without putting it in deps
  const hasMoreRef = useRef(true);

  useEffect(() => {
    if (!folder) return;

    const echo = getEcho();
    if (!echo) return; // SSR-safe

    const channelName = `folder.${folder}`;
    //const channelName = `folder.username`;

    echo.channel(channelName).listen(".file.added.deleted", (payload) => {
      console.log("New file arrived:", payload.data);
      setGallery((prev) => [...prev, payload.data]);
      //  optional: prepend new item
      // setGallery(prev => [payload, ...prev]);
    });

    return () => {
      echo.leave(channelName);
    };
  }, [folder]);

  useEffect(() => {
    hasMoreRef.current = hasMore;
  }, [hasMore]);

  // guard: auto-load page 1 only once per folder/request
  const firstPageLoadedRef = useRef(false);

  // ---------------- Message Sending ----------------
  const submitMessage = async (item, type) => {
    if (type === 1) sendEmail(item);
    else sendSMS(item);
  };

  const sendEmail = async (item) => {
    const { value: email } = await Swal.fire({
      title: "Enter your email",
      input: "email",
      inputLabel: "Email address",
      inputPlaceholder: "example@email.com",
      showCancelButton: true,
    });

    if (!email) return;

    try {
      const data = {
        type: 1,
        number: email,
        message: `https://bleepshare.tech/s/${item.short_code}`,
        custom: "Bleepshare Technology",
        folder: item.username,
        fileType: item.file_type,
      };

      await sendMessage(data);

      Swal.fire({
        icon: "success",
        title: "Email sent!",
        text: "The gallery link has been sent successfully.",
        timer: 2000,
        showConfirmButton: false,
      });
    } catch (err) {
      handleMessageError(err, "Invalid input");
    }
  };

  const sendSMS = async (item) => {
    const { value: phone } = await Swal.fire({
      title: "Enter mobile number",
      input: "text",
      inputLabel: "US Mobile Number",
      inputPlaceholder: "+15551234567",
      inputAttributes: {
        inputmode: "numeric",
        autocomplete: "tel",
      },
      showCancelButton: true,
      inputValidator: (value) => {
        if (!value) return "Mobile number is required";
        // const globalPhoneRegex = /^\+[1-9]\d{7,14}$/;
        // if (!globalPhoneRegex.test(value))
        //   return "Enter a valid mobile number (E.164 format, e.g. +15551234567)";
      },
    });

    if (!phone) return;

    try {
      const data = {
        type: 0,
        number: phone,
        message: `https://bleepshare.tech/s/${item.short_code}`,
        custom: "Bleepshare Technology",
        folder: item.username,
        fileType: item.file_type,
      };

      await sendMessage(data);

      Swal.fire({
        icon: "success",
        title: "SMS sent!",
        text: "The gallery link has been sent successfully.",
        timer: 2000,
        showConfirmButton: false,
      });
    } catch (err) {
      handleMessageError(err, "Invalid number");
    }
  };

  const handleMessageError = (err, defaultTitle) => {
    if (err?.status === 422) {
      Swal.fire({ icon: "warning", title: defaultTitle, text: err.message });
    } else if (err?.status === 401 || err?.status === 403) {
      Swal.fire({
        icon: "error",
        title: "Unauthorized",
        text: "You are not allowed to perform this action.",
      });
    } else if (err?.status >= 500) {
      Swal.fire({
        icon: "error",
        title: "Server error",
        text: "Technical issue. Please try again later.",
      });
    } else {
      Swal.fire({
        icon: "error",
        title: "Error",
        text: err?.message || "Something went wrong.",
      });
    }
  };

  // ---------------- Download / Share ----------------
  const handleDownload = async (item) => {
    try {
      const url = fileURL(item);
      if (!url) return;

      // Fetch file from API
      const response = await fetch(url);
      if (!response.ok) {
        throw new Error("Failed to download file");
      }

      // Convert response to blob
      const blob = await response.blob();

      // Create temporary download link
      const blobUrl = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = blobUrl;
      link.download = item.file || "download";

      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      // Clean up
      window.URL.revokeObjectURL(blobUrl);
    } catch (error) {
      console.error("Download error:", error);
    }
  };

  const handleShare = async (item) => {
    const url = fileURL(item);
    if (!url) return;

    if (navigator.share) {
      try {
        await navigator.share({
          title: "Shared Media",
          text: "Check this out",
          url,
        });
      } catch {
        // user cancelled
      }
    } else {
      await navigator.clipboard.writeText(url);
      alert("Link copied to clipboard");
    }
  };

  // ---------------- Load Gallery (NO folder dependency) ----------------
  const loadGalleryPage = useCallback(
    async (page, folderArg, activeRequestId) => {
      if (!folderArg) return;
      if (!hasMoreRef.current) return;
      if (loadingRef.current) return;

      loadingRef.current = true;
      setPageLoading(true);

      try {
        const response = await getGalleryPage(page, folderArg);

        // ignore old response
        if (requestIdRef.current !== activeRequestId) return;

        const newItems = response?.data?.data?.data ?? [];

        setGallery((prev) => [...prev, ...newItems]);
        setCurrentPage(page + 1);

        if (newItems.length === 0) {
          setHasMore(false);

          Swal.fire({
            icon: "info",
            title: "No data found",
            text: "There are no items available in this gallery.",
            confirmButtonColor: "#dc3545",
          });
        }
      } catch (error) {
        console.error("Error loading gallery:", error);
      } finally {
        if (requestIdRef.current === activeRequestId) {
          setPageLoading(false);
        }
        loadingRef.current = false;
      }
    },
    [],
  );

  // Load settings FIRST, then auto-load page 1 ONCE
  useEffect(() => {
    if (!folder) {
      navigate("/404");
      return;
    }

    let isMounted = true;
    const myRequestId = ++requestIdRef.current;

    // reset UI for new folder
    setSettings(null);
    setGallery([]);
    setCurrentPage(1);
    setHasMore(true);
    setSettingsLoading(true);
    setPageLoading(false);

    // reset guards for new folder
    loadingRef.current = false;
    hasMoreRef.current = true;
    firstPageLoadedRef.current = false;

    const run = async () => {
      try {
        const res = await getSettings(folder);

        if (!isMounted) return;
        if (requestIdRef.current !== myRequestId) return;

        if (res?.data?.error) {
          navigate("/404");
          return;
        }

        setSettings(res?.data?.data ?? null);

        setSettingsLoading(false);

        // auto load first page ONLY ONCE
        if (!firstPageLoadedRef.current) {
          firstPageLoadedRef.current = true;
          await loadGalleryPage(1, folder, myRequestId);
        }
      } catch (e) {
        if (!isMounted) return;
        navigate("/404");
      }
    };

    run();

    return () => {
      isMounted = false;
    };
  }, [folder, navigate, loadGalleryPage]);

  useEffect(() => {
    if (!settings) return;

    const { title, logo } = settings;

    document.title = title || "Bleepshare Technology";

    if (logo) {
      let link = document.querySelector("link[rel~='icon']");
      if (!link) {
        link = document.createElement("link");
        link.rel = "icon";
        document.head.appendChild(link);
      }
      link.href = BASE_URL + logo;
    }
  }, [settings]);

  // After first auto load, only button click loads next pages
  const onLoadMorePage = () => {
    if (pageLoading || !hasMore) return;
    loadGalleryPage(currentPage, folder, requestIdRef.current);
  };

  const showFullScreenLoader = settingsLoading && !settings;

  return (
    <>
      {settings && <BackgroundView settings={settings} />}

      {showFullScreenLoader ? (
        <div
          style={{
            minHeight: "70vh",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <div className="text-center">
            <div className="spinner-border" role="status" aria-hidden="true" />
            <div style={{ marginTop: 10 }}>Loading gallery...</div>
          </div>
        </div>
      ) : (
        <div className="container-fluid" style={{ padding: 10 }}>
          {/* Header Section */}
          <div className="row align-items-center mb-4">
            <div className="col-md-4">
              {settings && (
                <img
                  src={BASE_URL + settings.logo}
                  alt="Logo"
                  style={{ height: 150 }}
                />
              )}
            </div>

            <div className="col-md-4 text-center">
              <h1 className="m-0">{settings?.title}</h1>
            </div>

            <div className="col-md-4 d-flex justify-content-end">
              <a
                href={"https://bleepshare.tech/slideshow?folder=" + folder}
                target="_blank"
                rel="noopener noreferrer"
                className="btn btn-danger"
                style={{ height: 40, marginRight: 20 }}
              >
                View Slideshow
              </a>

              <a
                href={"https://bleepshare.tech/animated/?folder=" + folder}
                target="_blank"
                rel="noopener noreferrer"
                className="btn btn-warning"
                style={{ height: 40 }}
              >
                View Animated Gallery
              </a>
            </div>
          </div>

          {/* Gallery Section */}
          {settings && (
            <MediaCard
              settings={settings}
              gallery={gallery}
              folder={folder}
              submitMessage={submitMessage}
              handleDownload={handleDownload}
              handleShare={handleShare}
            />
          )}

          {/* Footer */}
          <div className="text-center mt-3">
            <button
              type="button"
              onClick={onLoadMorePage}
              className="btn btn-danger"
              disabled={pageLoading || !hasMore}
            >
              {pageLoading && (
                <span
                  className="spinner-border spinner-border-sm"
                  style={{ marginRight: 10 }}
                  role="status"
                  aria-hidden="true"
                />
              )}
              {!hasMore ? "No More Items" : "Load More"}
            </button>
          </div>
        </div>
      )}
    </>
  );
}

export default GalleryPage;
